// src/components/Games/CheckTheBalance.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CheckTheBalance.css';

// ─── Uses your existing API pattern ──────────────────────────────────────────
const API_ENDPOINT = '/api/balance';

// ─── ASCII Art Frames ─────────────────────────────────────────────────────────
const HUMAN_FRAMES = [
  [
    '    O    ',
    '   /|\\   ',
    '   / \\   ',
  ],
  [
    '    O    ',
    '   \\|/   ',
    '   | |   ',
  ],
  [
    '   \\O/   ',
    '    |    ',
    '   / \\   ',
  ],
];

const REPTILE_FRAMES = [
  [
    '  _===_  ',
    ' / o o \\ ',
    ' \\ sss / ',
    '  \\|||/  ',
  ],
  [
    '  _===_  ',
    ' / o o \\ ',
    '  > ^ <  ',
    '  ~|||~  ',
  ],
  [
    '  _===_  ',
    ' / O O \\ ',
    '  \\sss/  ',
    '  /|||\\ ',
  ],
];

// ─── Boot Sequence ────────────────────────────────────────────────────────────
const BOOT_SEQUENCE = [
  { text: '', delay: 300 },
  { text: '▓▓▓ THE BALANCE PROTOCOL ▓▓▓', delay: 500 },
  { text: '', delay: 200 },
  { text: 'Scanning biosphere signatures..............', delay: 350 },
  { text: 'Calibrating dominance metrics..............', delay: 300 },
  { text: 'Polling territorial control nodes..........', delay: 350 },
  { text: 'Cross-referencing historical substrate......', delay: 300 },
  { text: 'Decrypting power allocation ledger.........', delay: 400 },
  { text: '', delay: 200 },
  { text: '▓ BALANCE RETRIEVED ▓', delay: 600 },
  { text: '', delay: 200 },
];

// ─── Animated ASCII Column ────────────────────────────────────────────────────
function FactionDisplay({ side, score, totalPoints, frames, label, color, icon }) {
  const [frameIndex, setFrameIndex] = useState(0);
  const [particles, setParticles] = useState([]);
  const particleId = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setFrameIndex(prev => (prev + 1) % frames.length);
    }, 1400);
    return () => clearInterval(interval);
  }, [frames.length]);

  // Particle spawner — more particles for higher score
  useEffect(() => {
    const rate = Math.max(300, 1200 - (score / totalPoints) * 900);
    const interval = setInterval(() => {
      const id = particleId.current++;
      setParticles(prev => {
        if (prev.length > 15) return prev;
        return [...prev, {
          id,
          x: 10 + Math.random() * 80,
          char: icon,
          duration: 2 + Math.random() * 2,
        }];
      });
      setTimeout(() => {
        setParticles(prev => prev.filter(p => p.id !== id));
      }, 4000);
    }, rate);
    return () => clearInterval(interval);
  }, [score, totalPoints, icon]);

  const pct = Math.round((score / totalPoints) * 100);

  return (
    <div className={`ctb-faction ctb-faction-${side}`}>
      {/* Floating particles */}
      <div className="ctb-faction-particles">
        {particles.map(p => (
          <span
            key={p.id}
            className="ctb-particle"
            style={{
              left: `${p.x}%`,
              color,
              animationDuration: `${p.duration}s`,
            }}
          >
            {p.char}
          </span>
        ))}
      </div>

      <div className="ctb-faction-label" style={{ color }}>{label}</div>

      <div className="ctb-faction-score" style={{ color, textShadow: `0 0 12px ${color}66` }}>
        {score}
      </div>

      <div className="ctb-faction-pct" style={{ color: `${color}88` }}>
        {pct}%
      </div>

      {/* Vertical bar */}
      <div className="ctb-faction-bar-track">
        <div
          className="ctb-faction-bar-fill"
          style={{
            height: `${pct}%`,
            background: `linear-gradient(to top, ${color}11, ${color}aa)`,
            borderColor: color,
            boxShadow: `0 0 8px ${color}33`,
          }}
        />
      </div>

      {/* ASCII art */}
      <pre className="ctb-faction-ascii" style={{ color }}>
        {frames[frameIndex].join('\n')}
      </pre>

      {/* Score pips */}
      <div className="ctb-faction-pips">
        {Array.from({ length: totalPoints }).map((_, i) => (
          <span
            key={i}
            className={`ctb-pip ${i < score ? 'ctb-pip-active' : 'ctb-pip-empty'}`}
            style={i < score ? { color, textShadow: `0 0 4px ${color}` } : {}}
          >
            {i < score ? '█' : '░'}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Horizontal War Bar ───────────────────────────────────────────────────────
function WarBar({ humanScore, reptileScore, totalPoints }) {
  const humanPct = (humanScore / totalPoints) * 100;
  const reptilePct = (reptileScore / totalPoints) * 100;

  return (
    <div className="ctb-warbar">
      <span className="ctb-warbar-label ctb-warbar-label-left">HUMANS</span>
      <div className="ctb-warbar-track">
        <div className="ctb-warbar-fill ctb-warbar-fill-human" style={{ width: `${humanPct}%` }} />
        <div className="ctb-warbar-fill ctb-warbar-fill-reptile" style={{ width: `${reptilePct}%` }} />
        <div className="ctb-warbar-divider" style={{ left: `${humanPct}%` }}>⚔</div>
      </div>
      <span className="ctb-warbar-label ctb-warbar-label-right">REPTILES</span>
    </div>
  );
}

// ─── Intel Ticker ─────────────────────────────────────────────────────────────
function IntelTicker({ humanScore, reptileScore, lore }) {
  const [index, setIndex] = useState(0);

  const messages = [
    ...(lore || []),
    reptileScore > humanScore
      ? `REPTILIANS: ${reptileScore}–${humanScore}. The cold-blooded ones hold the advantage.`
      : reptileScore < humanScore
        ? `RESISTANCE: ${humanScore}–${reptileScore}. Mammalian forces push back.`
        : `EQUILIBRIUM: ${humanScore}–${reptileScore}. The balance holds... for now.`,
    'This is not a game. This is a ledger.',
    'The balance shifts. It always shifts.',
    'They have been here longer than you. Remember that.',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % messages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div className="ctb-ticker">
      <span className="ctb-ticker-prefix">[INTEL] </span>
      <span className="ctb-ticker-text" key={index}>{messages[index]}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function CheckTheBalance() {
  const [data, setData] = useState(null);
  const [bootComplete, setBootComplete] = useState(false);
  const [bootLines, setBootLines] = useState([]);
  const [glitch, setGlitch] = useState(false);

  // ── Fetch balance ───────────────────────────────────────────────────────────
  const fetchBalance = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINT);
      if (!res.ok) throw new Error(`${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Balance fetch failed:', err);
      // Hardcoded fallback so the game always works
      setData({
        humanScore: 3,
        reptileScore: 7,
        totalPoints: 10,
        lastUpdated: new Date().toISOString(),
        lore: [],
      });
    }
  }, []);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  // Poll every 60s
  useEffect(() => {
    const id = setInterval(fetchBalance, 60000);
    return () => clearInterval(id);
  }, [fetchBalance]);

  // ── Boot sequence ───────────────────────────────────────────────────────────
  useEffect(() => {
    let total = 0;
    const timeouts = [];

    BOOT_SEQUENCE.forEach((line) => {
      total += line.delay;
      timeouts.push(setTimeout(() => {
        setBootLines(prev => [...prev, line]);
      }, total));
    });

    timeouts.push(setTimeout(() => setBootComplete(true), total + 500));
    return () => timeouts.forEach(clearTimeout);
  }, []);

  // ── Random glitch ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!bootComplete) return;
    const id = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 150);
    }, 10000 + Math.random() * 15000);
    return () => clearInterval(id);
  }, [bootComplete]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const winnerText = () => {
    if (!data) return '';
    if (data.reptileScore > data.humanScore) return 'THE REPTILIANS HOLD DOMINION';
    if (data.humanScore > data.reptileScore) return 'HUMANITY LEADS — FOR NOW';
    return 'THE BALANCE IS PERFECTLY HELD';
  };

  const winnerColor = () => {
    if (!data) return '#888';
    if (data.reptileScore > data.humanScore) return '#00ff41';
    if (data.humanScore > data.reptileScore) return '#4a9eff';
    return '#ffaa00';
  };

  const fmtDate = (iso) => {
    if (!iso) return 'UNKNOWN';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className={`ctb-container ${glitch ? 'ctb-glitch' : ''}`}>

      {/* Boot */}
      {!bootComplete && (
        <div className="ctb-boot">
          {bootLines.map((line, i) => (
            <div key={i} className="ctb-boot-line">
              {line.text ? (
                <>
                  <span className="ctb-boot-prefix">[SYS] </span>
                  <span className="ctb-boot-text">{line.text}</span>
                </>
              ) : (
                <div style={{ height: '0.8em' }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Main */}
      {bootComplete && data && (
        <div className="ctb-main">

          <div className="ctb-title-block">
            <div className="ctb-title">▓▓▓ CHECK THE BALANCE ▓▓▓</div>
            <div className="ctb-subtitle">THIS IS THE CURRENT STATE OF THE WORLD</div>
          </div>

          <div className="ctb-winner" style={{ color: winnerColor() }}>
            {winnerText()}
          </div>

          {/* Score Display */}
          <div className="ctb-score-row">
            <span className="ctb-score-human">{data.humanScore}</span>
            <span className="ctb-score-sep"> — </span>
            <span className="ctb-score-reptile">{data.reptileScore}</span>
          </div>

          <WarBar
            humanScore={data.humanScore}
            reptileScore={data.reptileScore}
            totalPoints={data.totalPoints}
          />

          {/* Arena */}
          <div className="ctb-arena">
            <FactionDisplay
              side="left"
              score={data.humanScore}
              totalPoints={data.totalPoints}
              frames={HUMAN_FRAMES}
              label="▓ HUMANS ▓"
              color="#4a9eff"
              icon="☉"
            />

            <div className="ctb-center-divider">
              <div className="ctb-divider-line" />
              <div className="ctb-divider-vs">VS</div>
              <div className="ctb-divider-line" />
            </div>

            <FactionDisplay
              side="right"
              score={data.reptileScore}
              totalPoints={data.totalPoints}
              frames={REPTILE_FRAMES}
              label="▓ REPTILES ▓"
              color="#00ff41"
              icon="🐍"
            />
          </div>

          <IntelTicker
            humanScore={data.humanScore}
            reptileScore={data.reptileScore}
            lore={data.lore}
          />

          <div className="ctb-footer">
            <span>LAST SUBSTRATE SCAN: {fmtDate(data.lastUpdated)}</span>
            <span>TOTAL CONTROL POINTS: {data.totalPoints}</span>
            <span className="ctb-footer-warn">BUT REMEMBER, THE WORLD CAN CHANGE QUICKLY</span>
          </div>
        </div>
      )}

      {bootComplete && !data && (
        <div className="ctb-loading">SCANNING BIOSPHERE<span className="bbs-loading-dots" /></div>
      )}
    </div>
  );
}